import { useIsAdmin } from '@/app/providers/auth/authContext';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

import { ModeratorBar } from './ModeratorBar';
import {
  ActiveMatchesPanel,
  BoardsPanel,
  GroupsPanel,
  NextMatchesPanel,
} from './PresentationPanels';
import { WalkOnOverlay } from './WalkOnOverlay';
import { useRotatingPanel } from './useRotatingPanel';
import { useWalkOnQueue } from './useWalkOnQueue';

const ROTATION_MS = 12_000;

const PANELS = [
  { key: 'active', label: 'Aktuelle Spiele', Component: ActiveMatchesPanel },
  { key: 'next', label: 'Nächste Spiele', Component: NextMatchesPanel },
  { key: 'groups', label: 'Gruppen', Component: GroupsPanel },
  { key: 'boards', label: 'Boardbelegung', Component: BoardsPanel },
] as const;

/**
 * The public beamer / moderator view. No login required — it reads the same
 * live data as everything else and rotates through the panels on its own. A
 * signed-in director additionally sees the moderator bar for calling teams up.
 */
export function PresentationPage() {
  const { tournament, matches, phases, lookup, teams, isLoading, errorMessage } =
    useTournamentData();
  const isAdmin = useIsAdmin();

  usePageTitle('Live-Anzeige');

  const walkOn = useWalkOnQueue(matches, lookup, !isLoading && tournament !== undefined);
  const panelIndex = useRotatingPanel(PANELS.length, ROTATION_MS, walkOn.current !== null);

  if (isLoading) {
    return <LoadingScreen label="Live-Anzeige wird geladen …" />;
  }

  if (errorMessage !== null || tournament === undefined) {
    return <PageState title="Live-Anzeige nicht verfügbar" description={errorMessage ?? ''} />;
  }

  const panel = PANELS[panelIndex] ?? PANELS[0];
  const Component = panel.Component;
  const maxSong = tournament.settings.registration.songMaxDurationSeconds;

  const panelProps = {
    matches,
    phases,
    lookup,
    boardCount: tournament.settings.general.boardCount,
  };

  return (
    <div className="bg-canvas text-fg fixed inset-0 flex flex-col">
      <header className="border-line flex items-center justify-between border-b px-8 py-5">
        <div>
          <p className="text-brand text-sm font-semibold tracking-widest uppercase">
            Freiwillige Feuerwehr Ratekau
          </p>
          <h1 className="text-3xl font-black tracking-tight">{tournament.settings.general.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {PANELS.map((entry, index) => (
            <span
              key={entry.key}
              aria-hidden
              className={
                index === panelIndex
                  ? 'bg-brand h-2 w-8 rounded-full transition-all'
                  : 'bg-line h-2 w-2 rounded-full transition-all'
              }
            />
          ))}
        </div>
      </header>

      <main className="flex-1 overflow-hidden p-8">
        <Component {...panelProps} />
      </main>

      {isAdmin && <ModeratorBar teams={teams} onCallTeam={walkOn.enqueue} />}

      {walkOn.current !== null && (
        <WalkOnOverlay
          key={walkOn.current.id}
          team={walkOn.current}
          maxDurationSeconds={maxSong}
          onFinished={walkOn.dismiss}
        />
      )}
    </div>
  );
}
